import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Icon,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdArrowBack } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import reportsService from 'services/reportsService';

export default function TuitionPaymentsReport() {
  const navigate = useNavigate();
  const location = useLocation();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const primaryColor = useColorModeValue('brand.500', 'brand.400');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);

  const filters = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      center: searchParams.get('center') || '',
      field: searchParams.get('field') || '',
      phase: searchParams.get('phase') || '',
      status_filter: searchParams.get('status_filter') || '',
      prepa_session: searchParams.get('prepa_session') || '',
      skip: Number(searchParams.get('skip') || 0),
      limit: Number(searchParams.get('limit') || 100),
    };
  }, [location.search]);

  useEffect(() => {
    const fetchTuitionPayments = async () => {
      setLoading(true);
      setError('');

      const result = await reportsService.getTuitionPayments(filters);

      if (result.success) {
        setRows(result.data || []);
        setPagination(result.pagination || null);
      } else {
        setRows([]);
        setPagination(null);
        setError(result.error || 'Failed to load tuition payments report.');
      }

      setLoading(false);
    };

    fetchTuitionPayments();
  }, [filters]);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        <Flex align='center' justify='space-between' wrap='wrap' gap='12px'>
          <Button
            leftIcon={<Icon as={MdArrowBack} />}
            onClick={() => navigate('/admin/student-reports')}
            variant='outline'
            size='sm'
          >
            Back
          </Button>
          <Text color={primaryColor} fontSize={{ base: 'xl', md: '2xl' }} fontWeight='700'>
            Tuition Payments Report
          </Text>
        </Flex>

        <Card p={{ base: '16px', md: '24px' }} bg='transparent' boxShadow='none'>
          <Flex wrap='wrap' gap='10px' mb='16px'>
            <Badge colorScheme='blue' px='10px' py='6px' borderRadius='8px'>
              Center: {filters.center || 'All'}
            </Badge>
            <Badge colorScheme='purple' px='10px' py='6px' borderRadius='8px'>
              Field: {filters.field || 'All'}
            </Badge>
            <Badge colorScheme='orange' px='10px' py='6px' borderRadius='8px'>
              Phase: {filters.phase || 'All'}
            </Badge>
            <Badge colorScheme='green' px='10px' py='6px' borderRadius='8px'>
              Status: {filters.status_filter || 'All'}
            </Badge>
            <Badge colorScheme='teal' px='10px' py='6px' borderRadius='8px'>
              Session: {filters.prepa_session || 'All'}
            </Badge>
          </Flex>

          {loading ? (
            <Flex justify='center' py='40px'>
              <Spinner size='lg' color='brand.500' />
            </Flex>
          ) : error ? (
            <Alert status='error' borderRadius='12px'>
              <AlertIcon />
              <Text color={textColor}>{error}</Text>
            </Alert>
          ) : rows.length === 0 ? (
            <Text color={textColorSecondary}>No tuition payment data found for the selected filters.</Text>
          ) : (
            <Box overflowX='auto'>
              <Table variant='simple' minW='1100px'>
                <Thead>
                  <Tr>
                    <Th>Student</Th>
                    <Th>Matricule</Th>
                    <Th>Center</Th>
                    <Th>Field</Th>
                    <Th>Type</Th>
                    <Th>Status</Th>
                    <Th>Amount</Th>
                    <Th>Total Paid</Th>
                    <Th>Remaining</Th>
                    <Th>Session</Th>
                    <Th>Due Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((item) => (
                    <Tr key={item.id}>
                      <Td>{item.student_name || '-'}</Td>
                      <Td>{item.student_matricule || '-'}</Td>
                      <Td>{item.student_center || '-'}</Td>
                      <Td>{item.field || '-'}</Td>
                      <Td>{item.type || '-'}</Td>
                      <Td>{item.status || '-'}</Td>
                      <Td>{item.amount ?? '-'}</Td>
                      <Td>{item.total_paid ?? '-'}</Td>
                      <Td>{item.remaining_balance ?? '-'}</Td>
                      <Td>{item.prepa_session || '-'}</Td>
                      <Td>{item.due_date ? new Date(item.due_date).toLocaleDateString() : '-'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {pagination && !loading && !error && (
            <Text mt='14px' color={textColorSecondary} fontSize='sm'>
              Showing {pagination.count ?? rows.length} of {pagination.total ?? rows.length} records
            </Text>
          )}
        </Card>
      </Flex>
    </Box>
  );
}
